/**
 * Image Lightbox Component
 *
 * Displays an image in a full-screen overlay with zoom capabilities.
 * Closes on click outside, Escape key, or close button.
 * Supports zoom via mouse wheel and zoom buttons.
 */

import { renderIcon } from '../utils/icons';

/** Zoom configuration */
const ZOOM_SETTINGS = {
  min: 0.5,
  max: 3,
  step: 0.25,
  default: 1,
};

/**
 * Open an image in a lightbox overlay
 * @param imageUrl - URL of the image to display
 * @param caption - Optional caption text
 */
export function openImageLightbox(imageUrl: string, caption?: string): void {
  let currentZoom = ZOOM_SETTINGS.default;

  const overlay = document.createElement('div');
  overlay.className = `
    fixed inset-0 z-50 flex items-center justify-center
    bg-black/80 backdrop-blur-sm
    animate-in fade-in duration-200
  `;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', 'Image viewer');

  const container = document.createElement('div');
  container.className = 'relative max-w-4xl max-h-[90vh] p-4';

  // Control buttons container (top right)
  const controls = document.createElement('div');
  controls.className = 'absolute top-2 right-2 z-10 flex items-center gap-2';

  // Zoom out button
  const zoomOutButton = document.createElement('button');
  zoomOutButton.className = `
    p-2 rounded-full
    bg-black/50 hover:bg-black/70
    text-white hover:text-neutral-300
    transition-colors
  `;
  zoomOutButton.setAttribute('aria-label', 'Zoom out');
  zoomOutButton.appendChild(renderIcon('minus', { size: 'sm' }));
  controls.appendChild(zoomOutButton);

  // Zoom indicator
  const zoomIndicator = document.createElement('span');
  zoomIndicator.className = 'text-white text-sm min-w-[3rem] text-center';
  zoomIndicator.textContent = '100%';
  controls.appendChild(zoomIndicator);

  // Zoom in button
  const zoomInButton = document.createElement('button');
  zoomInButton.className = `
    p-2 rounded-full
    bg-black/50 hover:bg-black/70
    text-white hover:text-neutral-300
    transition-colors
  `;
  zoomInButton.setAttribute('aria-label', 'Zoom in');
  zoomInButton.appendChild(renderIcon('plus', { size: 'sm' }));
  controls.appendChild(zoomInButton);

  // Close button
  const closeButton = document.createElement('button');
  closeButton.className = `
    p-2 rounded-full
    bg-black/50 hover:bg-black/70
    text-white hover:text-neutral-300
    transition-colors ml-2
  `;
  closeButton.setAttribute('aria-label', 'Close');
  closeButton.appendChild(renderIcon('x', { size: 'md' }));
  controls.appendChild(closeButton);

  container.appendChild(controls);

  // Image wrapper for zoom transform
  const imgWrapper = document.createElement('div');
  imgWrapper.className = 'overflow-auto max-h-[80vh] flex items-center justify-center';

  // Image
  const img = document.createElement('img');
  img.src = imageUrl;
  img.alt = caption || 'Receipt image';
  img.className = 'max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl transition-transform duration-200';
  img.style.transform = `scale(${currentZoom})`;
  img.style.transformOrigin = 'center center';
  imgWrapper.appendChild(img);
  container.appendChild(imgWrapper);

  // Caption
  if (caption) {
    const captionDiv = document.createElement('p');
    captionDiv.className = 'mt-2 text-center text-sm text-neutral-400';
    captionDiv.textContent = caption;
    container.appendChild(captionDiv);
  }

  overlay.appendChild(container);

  // Update zoom display and apply to image
  function updateZoom(newZoom: number) {
    currentZoom = Math.min(ZOOM_SETTINGS.max, Math.max(ZOOM_SETTINGS.min, newZoom));
    img.style.transform = `scale(${currentZoom})`;
    zoomIndicator.textContent = `${Math.round(currentZoom * 100)}%`;

    // Update button states
    zoomOutButton.disabled = currentZoom <= ZOOM_SETTINGS.min;
    zoomInButton.disabled = currentZoom >= ZOOM_SETTINGS.max;
    zoomOutButton.style.opacity = currentZoom <= ZOOM_SETTINGS.min ? '0.5' : '1';
    zoomInButton.style.opacity = currentZoom >= ZOOM_SETTINGS.max ? '0.5' : '1';
  }

  // Zoom button handlers
  zoomInButton.addEventListener('click', (e) => {
    e.stopPropagation();
    updateZoom(currentZoom + ZOOM_SETTINGS.step);
  });

  zoomOutButton.addEventListener('click', (e) => {
    e.stopPropagation();
    updateZoom(currentZoom - ZOOM_SETTINGS.step);
  });

  // Mouse wheel zoom
  imgWrapper.addEventListener('wheel', (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_SETTINGS.step : ZOOM_SETTINGS.step;
    updateZoom(currentZoom + delta);
  });

  // Close on click outside (on overlay, not container)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeLightbox();
    }
  });

  // Keyboard handlers
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === '+' || e.key === '=') {
      updateZoom(currentZoom + ZOOM_SETTINGS.step);
    } else if (e.key === '-') {
      updateZoom(currentZoom - ZOOM_SETTINGS.step);
    } else if (e.key === '0') {
      updateZoom(ZOOM_SETTINGS.default);
    }
  };
  document.addEventListener('keydown', handleKeydown);

  // Close button click
  closeButton.addEventListener('click', () => closeLightbox());

  function closeLightbox() {
    overlay.classList.add('animate-out', 'fade-out', 'duration-150');
    setTimeout(() => {
      overlay.remove();
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = '';
    }, 150);
  }

  // Prevent body scroll while lightbox is open
  document.body.style.overflow = 'hidden';

  document.body.appendChild(overlay);
}

export default openImageLightbox;
